import { Controller, FieldPath, FieldValues, UseFormReturn } from "react-hook-form"
import { Flex } from "../../../../../components/flex/Flex"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { useIsMaxMd } from "../../../../../hooks/mediaHooks"
import { theme } from "../../../../../styles/stitches.config"
import { getFormError, getFormSuccess } from "../../../../../utils/form"
import { FormItem, FormLabel } from "../components/Form"
import { FormInput, InputDisclaimer } from "../components/Input"

type Props<T extends FieldValues> = {
  form: UseFormReturn<T>
  streetId: FieldPath<T>
  cityId: FieldPath<T>
  descriptiveNumberId: FieldPath<T>
  orientationNumberId: FieldPath<T>
  postalCodeId: FieldPath<T>
}

export function AddressFormSection<T extends FieldValues>(props: Props<T>) {
  const isMaxMd = useIsMaxMd()
  const rightContentCSS = isMaxMd ? undefined : { right: "0px" }
  const css = isMaxMd ? undefined : { paddingLeft: theme.space.s2_5 }

  const street = (
    <Controller
      control={props.form.control}
      name={props.streetId}
      render={({ field }) => (
        <FormItem
          css={{
            width: isMaxMd ? undefined : "29%",
          }}
        >
          <FormLabel>Ulice</FormLabel>

          <FormInput
            id={props.streetId}
            type="text"
            placeholder="např. Zahradní"
            {...field}
            error={getFormError({ form: props.form, name: props.streetId })}
            success={getFormSuccess({ form: props.form, name: props.streetId })}
          />
        </FormItem>
      )}
    />
  )

  const distinctionNumbers = (
    <>
      <FormItem
        css={{
          width: isMaxMd ? "100%" : "9.5%",
        }}
      >
        <FormLabel>č.p.</FormLabel>
        <FormInput
          id={props.descriptiveNumberId}
          type="number"
          {...props.form.register(props.descriptiveNumberId, { valueAsNumber: true })}
          error={getFormError({ form: props.form, name: props.descriptiveNumberId })}
          success={getFormSuccess({ form: props.form, name: props.descriptiveNumberId })}
          rightContentCSS={rightContentCSS}
          css={css}
        />
      </FormItem>

      <Controller
        control={props.form.control}
        name={props.orientationNumberId}
        render={({ field }) => (
          <FormItem
            css={{
              width: isMaxMd ? "100%" : "9.5%",
            }}
          >
            <FormLabel>č.o.</FormLabel>
            <FormInput
              id={props.orientationNumberId}
              type="text"
              {...field}
              error={getFormError({ form: props.form, name: props.orientationNumberId })}
              success={getFormSuccess({ form: props.form, name: props.orientationNumberId })}
              rightContentCSS={rightContentCSS}
              css={css}
            />
          </FormItem>
        )}
      />
    </>
  )

  const cityPart = (
    <>
      <Controller
        control={props.form.control}
        name={props.cityId}
        render={({ field }) => (
          <FormItem
            css={{
              width: isMaxMd ? "55%" : "24.5%",
            }}
          >
            <FormLabel>Město</FormLabel>
            <FormInput
              id={props.cityId}
              type="text"
              placeholder="např. Praha 10"
              {...field}
              error={getFormError({ form: props.form, name: props.cityId })}
              success={getFormSuccess({ form: props.form, name: props.cityId })}
            />
          </FormItem>
        )}
      />

      <Controller
        control={props.form.control}
        name={props.postalCodeId}
        render={({ field }) => (
          <FormItem
            css={{
              width: isMaxMd ? "45%" : "18%",
            }}
          >
            <FormLabel>PSČ</FormLabel>
            <FormInput
              id={props.postalCodeId}
              type="text"
              placeholder="5 číslic"
              {...field}
              error={getFormError({ form: props.form, name: props.postalCodeId })}
              success={getFormSuccess({ form: props.form, name: props.postalCodeId })}
            />
          </FormItem>
        )}
      />
    </>
  )
  const disclaimer = <InputDisclaimer label="Zadejte PSČ jako číslo bez mezery." />

  if (isMaxMd) {
    return (
      <>
        {street}
        <Spacer size={theme.space.s6} />
        <Flex css={{ gap: "5%" }}>{distinctionNumbers}</Flex>
        <Spacer size={theme.space.s6} />
        <Flex css={{ gap: "5%" }}>{cityPart}</Flex>
        <Spacer size={theme.space.s3_5} />

        {disclaimer}
      </>
    )
  }

  return (
    <>
      <Flex css={{ gap: theme.space.s4 }}>
        {street}
        {distinctionNumbers}
        {cityPart}
      </Flex>
      <Spacer size={theme.space.s3_5} />
      {disclaimer}
    </>
  )
}
